import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CurrentsAPI, type OceanOrgSkill } from "@vida/currents-core";
import { createCurrentsApiConfig } from "../services/currentsSession";

export function SkillsCatalogPage() {
  const [items, setItems] = useState<OceanOrgSkill[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const handleDelete = async (skill: OceanOrgSkill) => {
    const ok = window.confirm(
      `Eliminare la skill "${skill.name}"?\n\nGli agenti che la usano smetteranno di vederla.`,
    );
    if (!ok) return;
    setDeletingId(skill.id);
    try {
      await CurrentsAPI.deleteOrgSkill(createCurrentsApiConfig(), skill.id);
      setItems((prev) => prev.filter((s) => s.id !== skill.id));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setDeletingId(null);
    }
  };

  useEffect(() => {
    void (async () => {
      try {
        const skills = await CurrentsAPI.listOrgSkills(createCurrentsApiConfig(), undefined, false);
        setItems(skills);
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1>Skills</h1>
          <p className="muted">Catalogo org condiviso — capacità e tool riusabili tra gli agenti.</p>
        </div>
        <Link className="btn primary" to="/skills/new">
          Nuova skill
        </Link>
      </header>

      {loading && <p className="muted">Caricamento…</p>}
      {error && <p className="err">{error}</p>}

      {!loading && !error && items.length === 0 && (
        <div className="card">
          <p className="muted">Nessuna skill nel catalogo.</p>
        </div>
      )}

      {!loading && !error && items.length > 0 && (
        <div className="catalog-grid">
          {items.map((s) => (
            <article key={s.id} className="catalog-card">
              <div className="catalog-card-head">
                <span className="catalog-icon" aria-hidden>
                  {s.icon}
                </span>
                <div>
                  <h2>{s.name}</h2>
                  <code className="catalog-id">{s.id}</code>
                </div>
              </div>
              {s.description && <p className="muted">{s.description}</p>}
              <p className="catalog-meta">
                <span className="chip">{s.enabled ? "Attiva" : "Disabilitata"}</span>
                <span className="chip">{s.skillType ?? "TOOL_BUNDLE"}</span>
                {s.skillType === "SCRIPT" && (
                  <span className="muted">v{s.publishedScriptVersion ?? 0} pubblicata</span>
                )}
                {s.skillType === "MODEL" && <span className="muted">Binding modello</span>}
                {s.toolRefs.length > 0 && (
                  <span className="muted">Tool: {s.toolRefs.join(", ")}</span>
                )}
              </p>
              <p style={{ marginTop: "0.75rem", display: "flex", gap: "0.5rem" }}>
                <Link className="btn btn-sm" to={`/skills/${s.id}/edit`}>
                  {s.skillType === "SCRIPT"
                    ? "Editor script"
                    : s.skillType === "MODEL"
                      ? "Editor modello"
                      : "Dettaglio"}
                </Link>
                <button
                  type="button"
                  className="btn btn-sm"
                  disabled={deletingId === s.id}
                  onClick={() => void handleDelete(s)}
                >
                  {deletingId === s.id ? "Eliminazione…" : "Elimina"}
                </button>
              </p>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}
